import React from 'react';
import Tabs from './ui/Tabs';
import { formatCurrency } from '../utils/formatCurrency';
import { moneyOrAbsent } from '../utils/moneyOrAbsent';
import { roundMoney } from '../utils/money';

/**
 * THE activity-history LIST: a newest-first column of "who did what, when" with a
 * dot on a rail. Extracted 2026-10-29 from `OrderActivityHistory.js` (owner:
 * "should have a common component for warning popups, activity history") so the
 * timesheets history renders the same list instead of drawing a second one.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 PRESENTATIONAL ONLY. IT RESOLVES NOTHING.
 * ═══════════════════════════════════════════════════════════════════════════
 * No fetch, no permission test, no filtering, no sorting. The CALLER fetches,
 * decides who may see what, filters the rows for the active filter and hands over
 * finished `entries`; the disclosure header, laziness and memo stay in the caller
 * (`OrderActivityHistory` owns all three for orders).
 *
 * `entries` — NEWEST FIRST, exactly as given (the caller reverses an ASC feed;
 * this list never sorts):
 *   { id, at, actor, onBehalfOf?, tag?, details?, reason?, money?, dotClass? }
 *   • `at`       a date string (`2026-03-01 09:00:00` or ISO); formatted here as
 *                `1 Mar 2026, 09:00`; an unparseable value is shown as given.
 *   • `actor`    "by {actor}".
 *   • `tag`      the bold headline (orders: "Approved"; timesheets: "Hours edited").
 *   • `details`  a string (a `<p>`) or a node (a `<div>`, so a block child is legal).
 *   • `reason`   a required-reason line, "Reason: …".
 *   • `money`    `{ before, after }` → "£a → £b (up £x)" / "(down £x)" / "(no change)".
 *                Direction is TEXT as well as colour, never colour alone. A missing
 *                side shows the em dash (`moneyOrAbsent`); no delta is claimed then.
 *   • `dotClass` the rail dot's background class (orders colours it by action);
 *                default grey. Pass the literal class so Tailwind's scanner sees it.
 *   • `onBehalfOf` renders "(on behalf of X)" beside the actor. 🔒 THE COMPONENT
 *                DECIDES NOTHING ABOUT WHO MAY SEE IT. Owner, 2026-09-23: "site
 *                admin should not show up on audits for users to see" - the server
 *                returns the impersonating admin to a global admin ONLY, and a
 *                caller passes this prop only for rows it was given it for. The
 *                ORDER history has never rendered it and still does not (its
 *                adapter maps nothing here); do not add it there to "complete" the
 *                prop. A seated-user on-behalf (a manager approving for someone)
 *                is a legitimate row for any viewer - the caller says which it is.
 *
 * Optional `filters` ([{ id, label }]) + `activeFilter` + `onFilterChange(id)` draw
 * the house tab strip, `ui/Tabs`, above the list (owner review round 2026-10-30: a
 * filter row of a history reads as TABS, like every other list page; it was
 * `ui/SegmentedControl` until then); the caller filters `entries`. Testids:
 * `{prefix}-filters-tab-<id>` (Tabs' own `idPrefix` scheme).
 * `emptyText` is shown when there are no entries (the filter control stays so a
 * narrowed list can be widened). `testIdPrefix` → `{prefix}-entry`, `-empty`,
 * `-filters`, `-reason`, `-money` (bare names with no prefix would be too generic,
 * so the default is `activity`).
 *
 * 🪤 TEXT COLOURS ARE OWNED HERE, with their dark pairs (`components/CLAUDE.md`:
 * a shared presentational component never inherits colour). The greys are the
 * ones `OrderActivityHistory` shipped - byte-identical, pinned by
 * `OrderActivityHistory.rowMarkup.test.js`. `text-green-700` / `text-red-700` are
 * bare on purpose: `index.css` already carries `html.dark .text-green-700` and
 * `.text-red-700` with `!important`, so a `dark:` pair would compile and be inert.
 */

const TIME_FORMAT = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };

function formatAt(at) {
  const ts = at ? new Date(String(at).replace(' ', 'T')) : null;
  return ts && !isNaN(ts.getTime()) ? ts.toLocaleString('en-GB', TIME_FORMAT) : at;
}

function MoneyChange({ money, testId }) {
  const before = moneyOrAbsent(money.before);
  const after = moneyOrAbsent(money.after);
  const b = Number(money.before);
  const a = Number(money.after);
  const known = money.before != null && money.before !== '' && money.after != null && money.after !== ''
    && Number.isFinite(b) && Number.isFinite(a);
  let verdict = null;
  if (known) {
    const delta = roundMoney(roundMoney(a) - roundMoney(b));
    if (delta > 0) verdict = { text: `up ${formatCurrency(delta)}`, cls: 'text-green-700' };
    else if (delta < 0) verdict = { text: `down ${formatCurrency(Math.abs(delta))}`, cls: 'text-red-700' };
    else verdict = { text: 'no change', cls: 'text-gray-500 dark:text-gray-400' };
  }
  return (
    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-testid={testId}>
      {before} → {after}
      {verdict && <span className={`ml-1 font-medium ${verdict.cls}`}>({verdict.text})</span>}
    </p>
  );
}

export default function ActivityHistoryList({
  entries = [],
  filters,
  activeFilter,
  onFilterChange,
  filtersAriaLabel = 'Filter activity',
  emptyText = 'No activity recorded yet.',
  testIdPrefix = 'activity',
}) {
  const tid = (suffix) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix);

  const body = entries.length === 0 ? (
    <p className="text-sm text-gray-400 italic" data-testid={tid('empty')}>{emptyText}</p>
  ) : (
    <div className="relative">
      <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gray-200 dark:bg-gray-700"></div>
      <div className="space-y-4">
        {entries.map((entry) => (
          <div key={entry.id} className="relative pl-8" data-testid={tid('entry')}>
            <div className={`absolute left-1.5 top-1.5 w-3 h-3 rounded-full ${entry.dotClass || 'bg-gray-400'} border-2 border-white dark:border-gray-800 shadow`}></div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                {entry.tag && <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{entry.tag}</span>}
                <span className="text-xs text-gray-500 dark:text-gray-400">by {entry.actor}</span>
                {entry.onBehalfOf && (
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-testid={tid('on-behalf')}>(on behalf of {entry.onBehalfOf})</span>
                )}
              </div>
              {entry.details && (typeof entry.details === 'string' ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{entry.details}</p>
              ) : (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{entry.details}</div>
              ))}
              {entry.reason && (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic mt-0.5" data-testid={tid('reason')}>Reason: {entry.reason}</p>
              )}
              {entry.money && <MoneyChange money={entry.money} testId={tid('money')} />}
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatAt(entry.at)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (!filters || filters.length === 0) return body;
  return (
    <div>
      <Tabs
        tabs={filters}
        active={activeFilter}
        onChange={onFilterChange}
        ariaLabel={filtersAriaLabel}
        idPrefix={tid('filters')}
      />
      {body}
    </div>
  );
}
